/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { EllipsesAnimation } from '../../../common/ui/loading-ellipses';
import { LazyAnimatedRovoIcon } from '../../../common/ui/rovo-animated-icon';

const styles = cssMap({
	root: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'wrap',
		alignItems: 'center',
	},
	iconContainer: {
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	text: {
		color: token('color.text'),
		font: token('font.body'),
		wordBreak: 'break-word',
		flex: 1,
	},
	ellipsesContainer: {
		flex: 1,
	},
});

type RovoThinkingIndicatorProps = {
	// text shown between the Rovo icon and ellipses
	title: string;
	isAnimated?: boolean;
	// true = Rovo icon, ReactNode support to enable passing Agent icons
	icon?: boolean | ReactNode;
	testId?: string;
};

const getIcon = (icon: ReactNode | boolean): ReactNode => {
	if (
		(FeatureGates.getExperimentValue(
			'rovo_chat_explicit_thinking_deeper_experiment',
			'isThinkDeeperEnabled',
			false,
			{
				fireExperimentExposure: false,
			},
		) ||
			FeatureGates.getExperimentValue(
				'hybrid_orchestrator_complexity_experiment',
				'isEnabled',
				false,
				{
					fireExperimentExposure: false,
				},
			)) &&
		fg('rovo_td_compact_cot_master_gate')
	) {
		return null;
	}

	if (React.isValidElement(icon)) {
		return icon;
	} else if (icon) {
		if (fg('platform_editor_ai_aifc_patch_ga')) {
			return (
				<LazyAnimatedRovoIcon
					alt="Rovo"
					testId="rovo-loading-animation"
					width="24px"
					height="24px"
				/>
			);
		}
		return <RovoIcon label="" shouldUseHexLogo size="small" />;
	}

	return null;
};

export const RovoThinkingIndicator = ({
	title,
	isAnimated = true,
	icon = true,
	testId,
}: RovoThinkingIndicatorProps) => {
	return (
		<Box xcss={styles.root} testId={testId}>
			<Box xcss={styles.iconContainer}>
				{getIcon(icon)}
				<Box xcss={styles.text}>{title}</Box>
			</Box>
			<Box xcss={styles.ellipsesContainer}>
				<EllipsesAnimation isAnimated={isAnimated} />
			</Box>
		</Box>
	);
};
